/**
 * Wizard step 3, Details (W2-14 onboarding-wizard; DESIGN.md §4.3 "(3) DETAILS", §5.1 step 4).
 *
 *  - Title and artist, pre-filled from the song's tags or file name (marked as such until edited); illustrator;
 *    charter, remembered from the last chart (`settings.ext['wizard.charter']`, saved on Create).
 *  - Difficulty: label (EZ / HD / IN / AT / SP or a custom text) and level number 0-20 in steps of 0.1, previewed
 *    as the string the chart shows ("IN Lv.13"); the number is written explicitly as info.yml `difficulty`.
 *  - Song-select preview: start and end in seconds with Play, over the song's waveform (click to move the range);
 *    defaults inside the song, and Next stays disabled while the range is not inside the song (Phira refuses to
 *    open a chart whose preview ends after the music).
 */

import { useEffect, type ReactNode } from 'react';
import type { NewChartDraft, WizardStepProps } from '../../../app/registry/types';
import { Expr } from '../../../core/expr';
import type { ExprProgram } from '../../../core/expr/types';
import { Button, Icon, NumberField, Segmented, TextField } from '../../../ui';
import {
  cleanDifficulty,
  DIFFICULTY_MAX,
  DIFFICULTY_MIN,
  DIFFICULTY_STEP,
  difficultyForLabel,
  fitPreviewRange,
  formatLevel,
  isPresetLabel,
  latestPreviewEnd,
  LEVEL_LABELS,
  MAX_TEXT_LENGTH,
  previewRangeProblem,
  roundMs,
  type PreviewRangeProblem,
} from '../draft';
import { Waveform } from '../Waveform';
import { m } from '../wizard.messages';
import s from '../Wizard.module.css';
import { useServicesText, useSongPlayer, useStepActions, useWizardSession } from '../wizardSession';

type LabelChoice = (typeof LEVEL_LABELS)[number] | 'custom';

/** A committed number field value: a plain number, or an expression applied to the current value. */
export function committedNumber(
  v: { readonly kind: 'number'; readonly value: number } | { readonly kind: 'expr'; readonly program: ExprProgram },
  current: number,
): number | null {
  if (v.kind === 'number') return Number.isFinite(v.value) ? v.value : null;
  const r = Expr.apply(v.program, { v: current, i: 0, n: 1, u: 0 }, Expr.rng(1));
  return r === null || !Number.isFinite(r) ? null : r;
}

type TextKey = 'name' | 'composer' | 'illustrator' | 'charter';

function textPatch(field: TextKey, v: string): Partial<NewChartDraft> {
  switch (field) {
    case 'name':
      return { name: v };
    case 'composer':
      return { composer: v };
    case 'illustrator':
      return { illustrator: v };
    case 'charter':
      return { charter: v };
  }
}

const PROBLEM_TEXT: Readonly<Record<PreviewRangeProblem, string>> = {
  negative: m.previewNegative,
  order: m.previewOrder,
  beyondSong: m.previewBeyond,
  notFinite: m.previewNotFinite,
};

export function DetailsStep(props: WizardStepProps) {
  const { services, draft } = props;
  const { update, setCanContinue } = useStepActions(props);
  const text = useServicesText(services);
  const session = useWizardSession(props);
  const player = useSongPlayer(services);

  const music = draft.music;
  const song = music === null ? undefined : session.song(music.sha256);
  const duration = music?.durationSec ?? 0;
  const problem = music === null ? null : previewRangeProblem(draft.previewStart, draft.previewEnd, duration);
  const titleOk = draft.name.trim() !== '';
  const ok = music !== null && problem === null && titleOk;

  useEffect(() => {
    setCanContinue(ok);
  }, [ok, setCanContinue]);

  const labelChoice: LabelChoice = isPresetLabel(draft.levelLabel) ? draft.levelLabel : 'custom';
  const level = formatLevel(draft.levelLabel, draft.difficulty);

  const setRange = (start: number, end: number): void => {
    session.markPreviewEdited();
    update({ previewStart: roundMs(start), previewEnd: roundMs(end) });
  };

  const textRow = (label: string, value: string, field: TextKey, helpId: string, note?: ReactNode) => (
    <div className={s.fieldRow}>
      <TextField
        label={label}
        value={value}
        maxLength={MAX_TEXT_LENGTH}
        helpId={helpId}
        onChange={(v) => {
          update(textPatch(field, v));
        }}
        onCommit={(v) => {
          update(textPatch(field, v));
        }}
      />
      {note}
    </div>
  );

  return (
    <div className={s.step}>
      <div className={s.fieldGrid}>
        {textRow(
          text.t(m.fieldTitle),
          draft.name,
          'name',
          'wizard.title',
          !titleOk ? (
            <p className={s.fieldNote} data-kind="warning">
              <Icon name="TriangleAlert" size={14} />
              <span>{text.t(m.titleRequired)}</span>
            </p>
          ) : draft.name === session.autoName ? (
            <p className={s.fieldNote}>{text.t(m.prefilled)}</p>
          ) : undefined,
        )}
        {textRow(
          text.t(m.fieldArtist),
          draft.composer,
          'composer',
          'wizard.artist',
          draft.composer !== '' && draft.composer === session.autoComposer ? (
            <p className={s.fieldNote}>{text.t(m.prefilled)}</p>
          ) : undefined,
        )}
        {textRow(text.t(m.fieldIllustrator), draft.illustrator, 'illustrator', 'wizard.illustrator')}
        {textRow(
          text.t(m.fieldCharter),
          draft.charter,
          'charter',
          'wizard.charter',
          <p className={s.fieldNote}>{text.t(m.charterRemembered)}</p>,
        )}
      </div>

      <fieldset className={s.group}>
        <legend>{text.t(m.difficultyLabel)}</legend>
        <div className={s.difficultyRow}>
          <Segmented<LabelChoice>
            ariaLabel={text.t(m.difficultyLabel)}
            value={labelChoice}
            options={[
              ...LEVEL_LABELS.map((l) => ({ value: l, label: l })),
              { value: 'custom' as const, label: text.t(m.labelCustom) },
            ]}
            onChange={(v) => {
              const levelLabel = v === 'custom' ? '' : v;
              update({ levelLabel, difficulty: difficultyForLabel(draft.levelLabel, levelLabel, draft.difficulty) });
            }}
          />
          {labelChoice === 'custom' && (
            <TextField
              label={text.t(m.customLabel)}
              value={draft.levelLabel}
              maxLength={24}
              onChange={(v) => {
                update({ levelLabel: v });
              }}
              onCommit={(v) => {
                update({ levelLabel: v.trim() });
              }}
            />
          )}
          <NumberField
            label={text.t(m.levelNumber)}
            value={{ kind: 'value', value: draft.difficulty }}
            min={DIFFICULTY_MIN}
            max={DIFFICULTY_MAX}
            step={DIFFICULTY_STEP}
            precision={1}
            stacked
            helpId="wizard.difficulty"
            onCommit={(v) => {
              const n = committedNumber(v, draft.difficulty);
              if (n !== null) update({ difficulty: cleanDifficulty(n) });
            }}
          />
          <p className={s.levelPreview} aria-live="polite">
            {text.t(m.levelPreview, { level })}
          </p>
        </div>
      </fieldset>

      <fieldset className={s.group}>
        <legend>{text.t(m.previewTitle)}</legend>
        <p className={s.hint}>{text.t(m.previewHint)}</p>
        {music === null ? (
          <p className={s.hint}>{text.t(m.noSongYet)}</p>
        ) : (
          <>
            <div className={s.previewRow}>
              <NumberField
                label={text.t(m.previewStart)}
                value={{ kind: 'value', value: draft.previewStart }}
                min={0}
                max={latestPreviewEnd(duration)}
                step={0.1}
                precision={3}
                unit={text.t(m.unitSeconds)}
                helpId="wizard.previewStart"
                onCommit={(v) => {
                  const n = committedNumber(v, draft.previewStart);
                  if (n !== null) setRange(n, draft.previewEnd);
                }}
              />
              <NumberField
                label={text.t(m.previewEnd)}
                value={{ kind: 'value', value: draft.previewEnd }}
                min={0}
                max={latestPreviewEnd(duration)}
                step={0.1}
                precision={3}
                unit={text.t(m.unitSeconds)}
                helpId="wizard.previewEnd"
                onCommit={(v) => {
                  const n = committedNumber(v, draft.previewEnd);
                  if (n !== null) setRange(draft.previewStart, n);
                }}
              />
              {song !== undefined && (
                <Button
                  icon={player.playing === 'preview' ? 'Square' : 'Play'}
                  onClick={() => {
                    if (player.playing === 'preview') player.stop();
                    else player.play('preview', song, draft.previewStart, draft.previewEnd);
                  }}
                  disabledReason={problem === null ? null : { key: PROBLEM_TEXT[problem] }}
                >
                  {text.t(player.playing === 'preview' ? m.stop : m.previewPlay)}
                </Button>
              )}
            </div>
            {song !== undefined && (
              <Waveform
                song={song}
                label={text.t(m.previewWaveformLabel)}
                range={{ start: draft.previewStart, end: draft.previewEnd }}
                position={player.position}
                active={player.playing === 'preview'}
                onPick={(sec) => {
                  const len = Math.max(1, draft.previewEnd - draft.previewStart);
                  const r = fitPreviewRange(sec, sec + len, duration);
                  setRange(r.start, r.end);
                }}
              />
            )}
            {problem !== null && (
              <p className={s.error} role="alert">
                <Icon name="TriangleAlert" size={16} />
                <span>{text.t(PROBLEM_TEXT[problem], { max: latestPreviewEnd(duration) })}</span>
              </p>
            )}
          </>
        )}
      </fieldset>
    </div>
  );
}
